import { clsx } from "clsx";
import { useEffect, useRef } from "react";

/** Six single-digit boxes that behave like one field (paste, backspace, SMS autofill). */
export function CodeInput({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? "");

  useEffect(() => refs.current[0]?.focus(), []);

  function set(index: number, raw: string) {
    const clean = raw.replace(/\D/g, "");
    if (clean.length > 1) {
      const next = (value.slice(0, index) + clean).slice(0, 6);
      onChange(next);
      refs.current[Math.min(next.length, 5)]?.focus();
      return;
    }
    const arr = digits.slice();
    arr[index] = clean;
    onChange(arr.join("").slice(0, 6));
    if (clean && index < 5) refs.current[index + 1]?.focus();
  }

  return (
    <div className="flex justify-center gap-2 sm:gap-3" role="group" aria-label="6-digit code">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={d}
          disabled={disabled}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={i === 0 ? 6 : 1}
          aria-label={`Digit ${i + 1}`}
          onChange={(e) => set(i, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !d && i > 0) refs.current[i - 1]?.focus();
            if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
            if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
          }}
          onFocus={(e) => e.target.select()}
          className={clsx(
            "size-12 rounded-xl text-center font-display text-2xl ring-1 transition sm:size-14",
            d ? "bg-rust-50 ring-rust/40" : "bg-white ring-line",
            "focus:ring-2 focus:ring-rust focus:outline-none",
          )}
        />
      ))}
    </div>
  );
}
